<template>
  <div class="channel-select-wrapper">
    <div class="channel-select-label" v-if="label">
      <span v-if="required" class="required-asterisk">*</span>
      {{ label }}
    </div>
    <el-popover
      placement="bottom-start"
      trigger="manual"
      :width="width || 420"
      v-model="popoverVisible"
      popper-class="channel-select-popover"
    >
      <div class="channel-select-container" @click.stop>
        <div class="search-box">
          <el-input
            v-model="searchKeyword"
            placeholder="请输入渠道码ID/名称"
            size="small"
            clearable
            prefix-icon="el-icon-search"
          >
            <el-button slot="append" type="primary" size="small" @click.stop="handleSearch">
              搜索
            </el-button>
          </el-input>
        </div>
        <div class="channel-list" v-loading="loading">
          <div
            v-for="item in channelList"
            :key="item.id"
            class="channel-item"
            :class="{ selected: isItemSelected(item) }"
            @click.stop="handleSelectItem(item)"
          >
            <div class="channel-item-content">
              {{ item.name }}
            </div>
          </div>
          <div v-if="channelList.length === 0 && !loading" class="empty-data">暂无数据</div>
        </div>
      </div>

      <div slot="reference" class="reference-input">
        <el-input
          v-model="displayValue"
          :placeholder="placeholder || '请选择渠道码'"
          size="small"
          readonly
          suffix-icon="el-icon-arrow-down"
          @click.native.stop="togglePopover($event)"
        />
        <div class="selected-tags" v-if="selectedItems.length > 0 && !displayValue">
          <template v-if="collapseTags && selectedItems.length > collapseTagsLimit">
            <!-- 显示前N个标签 -->
            <el-tag
              v-for="item in selectedItems.slice(0, collapseTagsLimit)"
              :key="item.id"
              size="small"
              closable
              @close.stop="removeItem(item)"
            >
              {{ item.name }}
            </el-tag>

            <!-- 显示剩余数量的标签 -->
            <el-tag size="small" type="info" class="collapse-tag">
              +{{ selectedItems.length - collapseTagsLimit }}
            </el-tag>
          </template>
          <template v-else>
            <!-- 显示所有标签 -->
            <el-tag
              v-for="item in selectedItems"
              :key="item.id"
              size="small"
              closable
              @close.stop="removeItem(item)"
            >
              {{ item.name }}
            </el-tag>
          </template>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script>
  import { getChannelCodeNameList, getChannelDetail } from '@/api/channel'
  import { debounce } from 'lodash'

  export default {
    name: 'ChannelSelect',

    inject: ['flag'],
    props: {
      value: {
        type: [String, Number, Object, Array],
        default: () => []
      },
      apiUrl: {
        type: String,
        default: '/api/channel/list'
      },
      placeholder: {
        type: String,
        default: '请选择渠道码'
      },
      width: {
        type: Number,
        default: 420
      },
      disabled: {
        type: Boolean,
        default: false
      },
      label: {
        type: String,
        default: ''
      },
      required: {
        type: Boolean,
        default: false
      },
      multiple: {
        type: Boolean,
        default: true
      },
      params: {
        type: Object,
        default: () => ({})
      },
      // 是否折叠标签
      collapseTags: {
        type: Boolean,
        default: false
      },
      // 超过多少个标签时折叠显示
      collapseTagsLimit: {
        type: Number,
        default: 2
      },
      // 最大可选择的数量，0表示不限制
      maxSelections: {
        type: Number,
        default: 0
      }
    },
    data() {
      return {
        popoverVisible: false,
        searchKeyword: '',
        channelList: [],
        loading: false,
        selectedItems: [],
        displayValue: ''
      }
    },
    created() {
      // 创建点击外部关闭的事件处理函数，并绑定this上下文
      this.handleDocumentClick = this.handleClickOutside.bind(this)
    },
    watch: {
      value: {
        handler(val) {
          if (!val) {
            this.displayValue = ''
            this.selectedItems = []
            return
          }

          if (Array.isArray(val)) {
            // 多选模式
            if (val.length === 0) {
              this.selectedItems = []
              this.displayValue = ''
            } else if (val.every((item) => typeof item === 'object' && item !== null)) {
              // 如果所有值都是对象，直接使用
              this.selectedItems = [...val]
              this.updateDisplayValue()
            } else {
              // 如果是ID数组，需要获取详情
              this.fetchMultipleDetails(val)
            }
          } else {
            // 单选模式
            this.fetchChannelDetail(val)
          }
        },
        immediate: true,
        deep: true
      },
      selectedItems: {
        handler() {
          this.updateDisplayValue()
        },
        deep: true
      }
    },
    mounted() {
      if (!this.disabled) {
        this.searchChannels()
      }

      // 添加点击外部关闭下拉框的事件监听
      document.addEventListener('click', this.handleDocumentClick)
    },
    beforeDestroy() {
      // 移除事件监听，避免内存泄漏
      document.removeEventListener('click', this.handleDocumentClick)
    },
    methods: {
      EditEMailValid: debounce(({ vm, val, verifyEmail }) => {
        vm.searchChannels()
      }, 500),
      handleSearch() {
        this.EditEMailValid({ vm: this })
      },
      // 处理点击外部区域关闭下拉框
      handleClickOutside(event) {
        // 如果下拉框未显示，不处理
        if (!this.popoverVisible) return

        // 获取组件元素和popover元素
        const selectWrapper = this.$el
        const popover = document.querySelector('.channel-select-popover')

        // 检查点击是否发生在组件或弹出框外部
        if (
          selectWrapper &&
          !selectWrapper.contains(event.target) &&
          !(popover && popover.contains(event.target))
        ) {
          // 点击在组件和弹出框外部，关闭弹出框
          this.popoverVisible = false
        }
      },

      // 切换下拉框显示状态
      togglePopover(e) {
        if (this.disabled) return
        // 阻止事件冒泡，防止触发外部点击事件
        e && e.stopPropagation()
        this.popoverVisible = !this.popoverVisible

        // 如果打开下拉框，重新搜索
        if (this.popoverVisible) {
          this.searchChannels()
        }
      },

      // 更新显示值
      updateDisplayValue() {
        if (this.selectedItems.length === 0) {
          this.displayValue = ''
        } else if (!this.multiple || this.selectedItems.length === 1) {
          // 单选模式或只有一个选中项时，直接显示
          const item = this.selectedItems[0]
          this.displayValue = `${item.name}`
        } else {
          // 多选时不在输入框中显示，而是使用标签显示
          this.displayValue = ''
        }
      },

      // 搜索渠道
      searchChannels() {
        this.loading = true
        const params = {
          name: this.searchKeyword ? [this.searchKeyword] : [],
          size: 100,
          page: 1,
          type: this.flag,
          ...this.params
        }

        getChannelCodeNameList(params)
          .then((response) => {
            const { code, data, msg } = response
            if (code === 0 && data) {
              this.channelList = Array.isArray(data)
                ? data.map((item) => {
                    return {
                      name: item,
                      id: item
                    }
                  })
                : Array.isArray(data)
                ? data
                : []
            } else {
              this.$message.error(msg || '获取列表失败')
              this.channelList = []
            }
          })
          .catch((error) => {
            console.error('获取列表失败：', error)
            this.channelList = []
          })
          .finally(() => {
            this.loading = false
          })
      },

      // 获取多个渠道详情
      fetchMultipleDetails(ids) {
        if (!ids || ids.length === 0) return

        this.loading = true
        const promises = ids.map((id) => this.fetchChannelDetailWithoutLoading(id))

        Promise.all(promises)
          .then((results) => {
            const validResults = results
              .filter((res) => res && res.code === 200 && res.data)
              .map((res) => res.data)

            this.selectedItems = validResults
            this.updateDisplayValue()
          })
          .catch((error) => {
            console.error('获取多个详情失败：', error)
          })
          .finally(() => {
            this.loading = false
          })
      },

      // 不显示加载状态的详情获取
      fetchChannelDetailWithoutLoading(id) {
        if (!id) return Promise.resolve(null)

        return getChannelDetail(id)
      },

      // 获取渠道详情
      fetchChannelDetail(id) {
        if (!id) return

        this.loading = true
        getChannelDetail(id)
          .then((response) => {
            const { code, data, msg } = response
            if (code === 200 && data) {
              this.selectedItems = [data]
              this.updateDisplayValue()
            } else {
              console.warn('获取详情失败：', msg)
              this.displayValue = id
            }
          })
          .catch((error) => {
            console.error('获取详情失败：', error)
            this.displayValue = id
          })
          .finally(() => {
            this.loading = false
          })
      },

      // 选择一个渠道
      handleSelectItem(item) {
        if (this.multiple) {
          // 多选模式
          const index = this.selectedItems.findIndex((selected) => selected.id === item.id)

          if (index > -1) {
            // 如果已选中，则取消选中
            this.selectedItems.splice(index, 1)
          } else {
            // 如果设置了最大选择数量，且已经达到限制，则提示并返回
            if (this.maxSelections > 0 && this.selectedItems.length >= this.maxSelections) {
              this.$message.warning(`最多只能选择${this.maxSelections}项`)
              return
            }

            // 如果未选中，则添加到选中列表
            this.selectedItems.push(item)
          }

          // 发送给父组件
          this.$emit('input', [...this.selectedItems])
          this.$emit('select', [...this.selectedItems])

          // 选择后不关闭弹窗
        } else {
          // 单选模式
          this.selectedItems = [item]

          // 发送给父组件
          this.$emit('input', item)
          this.$emit('select', item)

          // 关闭弹窗
          this.popoverVisible = false
        }
      },

      // 移除选中项
      removeItem(item) {
        const index = this.selectedItems.findIndex((selected) => selected.id === item.id)
        if (index > -1) {
          this.selectedItems.splice(index, 1)

          // 发送给父组件
          this.$emit('input', [...this.selectedItems])
          this.$emit('select', [...this.selectedItems])
        }
      },

      // 检查项目是否已选中
      isItemSelected(item) {
        return this.selectedItems.some((selected) => selected.id === item.id)
      }
    }
  }
</script>

<style lang="scss" scoped>
  .channel-select-wrapper {
    display: inline-block;
    width: 300px;
    position: relative;

    .channel-select-label {
      font-size: 14px;
      color: #606266;
      line-height: 40px;
      padding: 0 0 10px;

      .required-asterisk {
        color: #f56c6c;
        margin-right: 4px;
      }
    }

    .reference-input {
      position: relative;
      width: 100%;
      cursor: pointer;

      .selected-tags {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        padding: 0 30px 0 5px;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px;
        pointer-events: none;
        overflow: hidden;

        .el-tag {
          pointer-events: auto;
          margin-right: 4px;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .collapse-tag {
          background-color: #909399;
          border-color: #909399;
          color: #fff;
        }
      }
    }
  }

  ::v-deep .channel-select-popover {
    background-color: #f5f7fa;
    padding: 0;
    border: 1px solid #dcdfe6;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    .el-popover__title {
      margin: 0;
      padding: 10px 0;
      border-bottom: 1px solid #ebeef5;
    }

    .popper__arrow {
      display: none !important;
    }
  }

  .channel-select-container {
    .search-box {
      padding: 10px;
      background-color: #f5f7fa;

      ::v-deep .el-input__inner {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      ::v-deep .el-button {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        margin: 0;
      }
    }

    .channel-list {
      max-height: 280px;
      overflow-y: auto;
      padding: 0;
      background-color: #fff;

      .channel-item {
        padding: 10px 15px;
        cursor: pointer;
        transition: background-color 0.3s;

        &:hover {
          background-color: #f5f7fa;
        }

        &.selected {
          background-color: #e6f1fc;
        }

        & + .channel-item {
          border-top: 1px solid #ebeef5;
        }

        .channel-item-content {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .empty-data {
        padding: 30px 0;
        text-align: center;
        color: #909399;
      }
    }
  }
</style>
